// =====================================================
// TEMPLATE MANAGEMENT PAGE
// TMOS Platform - Unified Template Management Interface
// =====================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../state/auth/AuthProvider';
import { useLanguage } from '../../state/language/LanguageProvider';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { TemplateVisibilityDropdown } from '../../components/templates/TemplateVisibilityDropdown';
import { TemplateAccessManagementModal } from '../../components/templates/TemplateAccessManagementModal';
import { CopyFromHierarchyModal } from '../../components/hierarchy/CopyFromHierarchyModal';
import { templateEntity } from '../../entities/TemplateEntity';
import { templateCRUDService } from '../../services/templateCRUDService';
import { Template } from '../../services/templateService';
import toast from 'react-hot-toast';

const TemplateManagementPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<any>({});
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [accessTemplate, setAccessTemplate] = useState<Template | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);

  // Filter templates by current user
  useEffect(() => {
    if (user?.id) {
      setFilters({ created_by: user.id });
    }
  }, [user?.id]);

  // Create a custom service that includes the current filters
  const createFilteredService = () => {
    return {
      ...templateCRUDService,
      getAll: async (params: any = {}) => {
        // Merge the current filters with the params
        const mergedParams = { ...filters, ...params };
        return await templateCRUDService.getAll(mergedParams);
      }
    };
  };

  // Position templates (from position_templates table) use prefixed id; manage them in Admin.
  const isPositionTemplateRow = (t: Template) =>
    typeof t.id === 'string' && t.id.startsWith('position-template-');

  // Handle template actions
  const handleEdit = (template: Template) => {
    if (isPositionTemplateRow(template)) {
      const ptId = template.id.replace(/^position-template-/, '');
      navigate('/admin/position-templates', { state: { highlightTemplateId: ptId } });
      return;
    }

    if (template.template_type === 'ROADMAP') {
      navigate(`/templates/comprehensive-builder?edit=${template.id}`);
      return;
    }

    if (template.template_type === 'PROJECT_STRUCTURE') {
      navigate(`/templates/project-structure-builder?edit=${template.id}`);
      return;
    }

    if (template.template_type === 'HIERARCHY') {
      // Navigate to template hierarchy editor page (reuses existing hierarchy UI)
      navigate(`/templates/hierarchy/${template.id}/edit`);
      return;
    }

    toast.error(t('templates.errors.templateTypeNotSupported') || 'This template type is not supported yet');
  };

  const handleDelete = async (template: Template) => {
    if (isPositionTemplateRow(template)) {
      toast(t('templates.positionTemplateManageInAdmin') || 'Position templates are managed in Admin → Position Templates.');
      return;
    }
    try {
      await templateCRUDService.delete(template.id);
      // The table will refresh automatically
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(t('templates.errors.failedToDeleteTemplate'));
    }
  };

  const handleCreate = () => {
    setIsTypeModalOpen(true);
  };

  const handleCopy = async (template: Template) => {
    if (isPositionTemplateRow(template)) {
      toast(t('templates.positionTemplateManageInAdmin') || 'Position templates are managed in Admin → Position Templates.');
      return;
    }
    try {
      if (!user?.id) {
        alert(t('templates.errors.mustBeLoggedInToCopy'));
        return;
      }

      // Get the original template with full details
      const originalTemplate = await templateCRUDService.findById(template.id);
      
      // Find existing copies created by current user to determine the next copy number
      const allTemplates = await templateCRUDService.findAll({ created_by: user.id });
      const baseName = originalTemplate.name;
      
      // Extract base name (remove any existing "Copy (n)" or "Copy" suffix)
      const copyPattern = /^(.+?)\s+Copy\s+\((\d+)\)$/;
      const copyOnlyPattern = /^(.+?)\s+Copy$/;
      
      let cleanBaseName = baseName;
      const copyMatch = baseName.match(copyPattern);
      const copyOnlyMatch = baseName.match(copyOnlyPattern);
      
      if (copyMatch) {
        cleanBaseName = copyMatch[1];
      } else if (copyOnlyMatch) {
        cleanBaseName = copyOnlyMatch[1];
      }
      
      // Find the highest copy number for templates starting with the base name
      let maxCopyNumber = 0;
      allTemplates.forEach(t => {
        // Skip the original template we're copying from
        if (t.id === template.id) {
          return;
        }
        
        if (t.name === cleanBaseName) {
          // Another template with the same base name exists, but we don't count it as a copy
          return;
        }
        
        if (t.name === `${cleanBaseName} Copy`) {
          // Found "Copy" without number - treat as Copy (1)
          if (maxCopyNumber < 1) {
            maxCopyNumber = 1;
          }
          return;
        }
        
        // Check for "Copy (n)" pattern
        const nameMatch = t.name.match(copyPattern);
        if (nameMatch && nameMatch[1] === cleanBaseName) {
          const copyNum = parseInt(nameMatch[2], 10);
          if (copyNum > maxCopyNumber) {
            maxCopyNumber = copyNum;
          }
        }
      });
      
      // Generate new name with next copy number
      const newCopyNumber = maxCopyNumber + 1;
      const newName = newCopyNumber === 1 
        ? `${cleanBaseName} Copy` 
        : `${cleanBaseName} Copy (${newCopyNumber})`;
      
      // Prepare copy data
      const copyData: any = {
        tenant_id: user.tenantId || originalTemplate.tenant_id,
        category_id: originalTemplate.category_id,
        name: newName,
        description: originalTemplate.description,
        template_type: originalTemplate.template_type,
        content: originalTemplate.content, // Deep copy the content
        marketplace_visibility: 'DRAFT', // Make copies draft by default
        version: '1.0', // Reset version
        created_by: user.id // Set to current user
      };
      
      // Create the copy
      await templateCRUDService.create(copyData);
      
      // Trigger table refresh
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error copying template:', error);
      alert(t('templates.errors.failedToCopyTemplate'));
    }
  };

  // Handle visibility change
  const handleVisibilityChange = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle access management
  const handleManageAccess = (template: Template) => {
    setAccessTemplate(template);
    setIsAccessModalOpen(true);
  };

  // Custom actions for templates - Visibility dropdown
  const customActions: any[] = [];

  // Update the entity to add custom renderer for visibility column
  const entityWithVisibilityRenderer = {
    ...templateEntity,
    tableColumns: templateEntity.tableColumns.map((col: any) => {
      if (col.key === 'marketplace_visibility') {
        return {
          ...col,
          render: (_value: any, item: Template) => {
            return (
              <TemplateVisibilityDropdown
                template={item}
                onVisibilityChange={handleVisibilityChange}
                onManageAccess={() => handleManageAccess(item)}
              />
            );
          }
        };
      }
      return col;
    })
  };

  const templateTypeOptions = useMemo(
    () => [
      {
        value: 'PROJECT_STRUCTURE',
        label: t('templates.types.projectStructure') || 'Project Structure',
        description:
          t('templates.projectStructureDescription') ||
          'Reusable inchstone hierarchies with start/end weeks',
        enabled: true
      },
      {
        value: 'ROADMAP',
        label: t('templates.types.roadmap') || 'Roadmap',
        description: t('templates.roadmapDescription') || 'Vision → Strategy → Program templates',
        enabled: true
      },
      {
        value: 'HIERARCHY',
        label: t('templates.types.hierarchy') || 'Hierarchy',
        description: t('templates.hierarchyDescription') || 'Create business, functional, or geographic hierarchy templates',
        enabled: true
      },
      {
        value: 'PROCESS_AREAS',
        label: t('templates.types.processAreas') || 'Process Areas',
        description: t('templates.processAreasDescription') || 'Process and subprocess sets; create from Process Admin.',
        enabled: true
      },
      {
        value: 'POSITION',
        label: t('templates.types.position') || 'Position',
        description: t('templates.comingSoonDescription') || 'Coming soon',
        enabled: false
      },
      {
        value: 'KPI',
        label: t('templates.types.kpi') || 'KPI',
        description: t('templates.comingSoonDescription') || 'Coming soon',
        enabled: false
      }
    ],
    [t]
  );

  const handleTemplateTypeSelection = (type: string) => {
    setIsTypeModalOpen(false);
    if (type === 'PROJECT_STRUCTURE') {
      navigate('/templates/project-structure-builder');
      return;
    }
    if (type === 'ROADMAP') {
      navigate('/templates/comprehensive-builder');
      return;
    }
    if (type === 'HIERARCHY') {
      setIsHierarchyModalOpen(true);
      return;
    }
    if (type === 'PROCESS_AREAS') {
      navigate('/admin/process-admin');
      toast(t('templates.processAreasCreateHint') || 'Select processes and use "Save as template" to create a Process Areas template.');
      return;
    }
    toast(t('common.comingSoon') || 'Coming soon');
  };

  const handleHierarchyTemplateSuccess = () => {
    setIsHierarchyModalOpen(false);
    setRefreshTrigger(prev => prev + 1);
    toast.success(t('templates.hierarchyTemplateCreated') || 'Hierarchy template created successfully');
  };


  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--color-surface-text)] mb-4">{t('templates.authenticationRequired')}</h2>
          <p className="text-[var(--color-secondary-text)]">{t('templates.pleaseLogInToManage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-2">
        {/* Header */}
        <div className="mb-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{t('templates.myTemplates')}</h1>
              <p className="mt-0.5 text-sm text-[var(--color-secondary-text)]">
                {t('templates.createManagePublishDescription')}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreate}
                className="bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] px-4 py-2 rounded-lg hover:bg-[var(--color-accent-bg)]/90 transition-colors"
              >
                {t('templates.createTemplate')}
              </button>
              <button
                onClick={() => navigate('/marketplace')}
                className="bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-[var(--color-border)] px-4 py-2 rounded-lg hover:bg-[var(--color-accent-bg)]/20 transition-colors"
              >
                {t('templates.browseMarketplace')}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-[var(--color-border)] p-2 mb-2">
          <h3 className="text-sm font-medium text-[var(--color-surface-text)] mb-2">{t('templates.filters')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t('templates.templateType')}
              </label>
              <select
                value={filters.template_type || ''}
                onChange={(e) => setFilters({ ...filters, template_type: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
              >
                <option value="">{t('templates.allTypes') || 'All Types'}</option>
                <option value="ROADMAP">{t('templates.types.roadmap') || 'Roadmap'}</option>
                <option value="PROJECT_STRUCTURE">{t('templates.types.projectStructure') || 'Project Structure'}</option>
                <option value="HIERARCHY">{t('templates.types.hierarchy') || 'Hierarchy'}</option>
                <option value="PROCESS_AREAS">{t('templates.types.processAreas') || 'Process Areas'}</option>
                <option value="POSITION">{t('templates.types.position') || 'Position'}</option>
                <option value="ROLE">{t('templates.types.role') || 'Role'}</option>
                <option value="USER">{t('templates.types.user') || 'User'}</option>
                <option value="KPI">{t('templates.types.kpi') || 'KPI'}</option>
                <option value="REPORT">{t('templates.types.report') || 'Report'}</option>
                <option value="RISK">{t('templates.types.risk') || 'Risk'}</option>
                <option value="COMMUNICATION">{t('templates.types.communication') || 'Communication'}</option>
                <option value="CHANGE_MANAGEMENT">{t('templates.types.changeManagement') || 'Change Management'}</option>
                <option value="PROFILE">{t('templates.types.profile') || 'Profile'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t('templates.industry')}
              </label>
              <select
                value={filters.industry_type || ''}
                onChange={(e) => setFilters({ ...filters, industry_type: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
              >
                <option value="">{t('templates.allIndustries') || 'All Industries'}</option>
                <option value="General">{t('templates.industries.general') || 'General'}</option>
                <option value="Cross-Industry">{t('templates.industries.crossIndustry') || 'Cross-Industry'}</option>
                <option value="Healthcare">{t('templates.industries.healthcare') || 'Healthcare'}</option>
                <option value="Finance">{t('templates.industries.finance') || 'Finance'}</option>
                <option value="Technology">{t('templates.industries.technology') || 'Technology'}</option>
                <option value="Manufacturing">{t('templates.industries.manufacturing') || 'Manufacturing'}</option>
                <option value="Retail">{t('templates.industries.retail') || 'Retail'}</option>
                <option value="Education">{t('templates.industries.education') || 'Education'}</option>
                <option value="Government">{t('templates.industries.government') || 'Government'}</option>
                <option value="Non-Profit">{t('templates.industries.nonProfit') || 'Non-Profit'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t('templates.marketplaceVisibility')}
              </label>
              <select
                value={filters.marketplace_visibility || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({ 
                    ...filters, 
                    marketplace_visibility: value === '' ? undefined : value as 'DRAFT' | 'PRIVATE' | 'PUBLIC'
                  });
                }}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
              >
                <option value="">{t('templates.allVisibility')}</option>
                <option value="DRAFT">{t('templates.draft')}</option>
                <option value="PRIVATE">{t('templates.private')}</option>
                <option value="PUBLIC">{t('templates.public')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Templates Table */}
        <div>
          <FlexibleCRUDTable
            key={`${JSON.stringify(filters)}-${refreshTrigger}`} // Re-render when filters or refresh trigger change
            entity={entityWithVisibilityRenderer}
            service={createFilteredService()}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCopy={handleCopy}
            onCreate={handleCreate}
            customActions={customActions}
            searchPlaceholder={t('templates.searchPlaceholder')}
            showCreateButton={false}
            showSearch={true}
            showPagination={true}
            pageSize={10}
            className="min-h-[400px]"
          />
        </div>
      </div>

      {/* Access Management Modal */}
      {isAccessModalOpen && accessTemplate && (
        <TemplateAccessManagementModal
          isOpen={isAccessModalOpen}
          onClose={() => {
            setIsAccessModalOpen(false);
            setAccessTemplate(null);
          }}
          template={accessTemplate}
          onAccessChange={() => {
            setRefreshTrigger(prev => prev + 1);
          }}
        />
      )}

      {isTypeModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-[var(--color-surface-bg)] rounded-2xl shadow-xl max-w-3xl w-full border border-[var(--color-border)]">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-[var(--color-surface-text)]">
                  {t('templates.selectTemplateType') || 'Select template type'}
                </h3>
                <p className="text-sm text-[var(--color-secondary-text)]">
                  {t('templates.selectTemplateTypeDescription') ||
                    'Choose the template experience you want to build.'}
                </p>
              </div>
              <button
                onClick={() => setIsTypeModalOpen(false)}
                className="text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] text-2xl leading-none"
                aria-label={t('common.close') || 'Close'}
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-6">
              {templateTypeOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => option.enabled && handleTemplateTypeSelection(option.value)}
                  disabled={!option.enabled}
                  className={`text-left border border-[var(--color-border)] rounded-xl p-4 transition focus:outline-none ${
                    option.enabled
                      ? 'hover:border-[var(--color-accent-bg)] hover:shadow focus:ring-2 focus:ring-[var(--color-accent-bg)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]'
                      : 'opacity-60 cursor-not-allowed bg-[var(--color-secondary-bg)]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-[var(--color-surface-text)]">{option.label}</h4>
                    {!option.enabled && (
                      <span className="text-xs bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] px-2 py-1 rounded-full">
                        {t('common.comingSoon') || 'Coming soon'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--color-secondary-text)]">{option.description}</p>
                </button>
              ))}
            </div>
            <div className="border-t border-[var(--color-border)] px-6 py-4 text-right">
              <button
                onClick={() => setIsTypeModalOpen(false)}
                className="px-4 py-2 text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hierarchy Template Creation Modal */}
      <CopyFromHierarchyModal
        isOpen={isHierarchyModalOpen}
        onClose={() => {
          setIsHierarchyModalOpen(false);
        }}
        onSuccess={handleHierarchyTemplateSuccess}
      />
    </div>
  );
};

export default TemplateManagementPage;